import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Star, Target } from "lucide-react";
import type { PointsBreakdownTipDto } from "@/types/api";
import { statsApi } from "@/api/stats";
import { useScope } from "@/app/useScope";
import { TeamFlag } from "@/components/TeamFlag";
import { Dialog, DialogBody, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { isGroupStage, stageLabel } from "@/lib/stage";
import { cn } from "@/lib/utils";

interface Props {
  open: boolean;
  onClose: () => void;
  userId?: string;
  playerName?: string;
}

export function PointsBreakdownModal({ open, onClose, userId, playerName }: Props) {
  const scope = useScope();
  const q = useQuery({
    queryKey: ["pointsBreakdown", scope.leagueId, scope.tournamentId, userId ?? "me"],
    queryFn: () => statsApi.pointsBreakdown(scope, userId),
    enabled: open,
  });

  const summary = useMemo(() => {
    const d = q.data;
    if (!d) return null;
    let group = 0;
    let knockout = 0;
    for (const t of d.tips) {
      if (isGroupStage(t.stage)) group += t.points;
      else knockout += t.points;
    }
    return { group, knockout, bonus: d.bonusPoints, total: d.totalPoints };
  }, [q.data]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{playerName ?? q.data?.playerName ?? "Pontok részletezése"}</DialogTitle>
        </DialogHeader>

        <DialogBody className="space-y-4">
          {q.isLoading && (
            <div className="flex justify-center py-8">
              <Loader2 className="size-5 animate-spin text-muted-foreground" />
            </div>
          )}

          {summary && (
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <SummaryTile label="Csoportkör" value={summary.group} />
              <SummaryTile label="Kieséses" value={summary.knockout} />
              <SummaryTile label="Bónuszok" value={summary.bonus} />
              <SummaryTile label="Összesen" value={summary.total} primary />
            </div>
          )}

          {q.data && q.data.bonuses.length > 0 && (
            <div>
              <SectionTitle>Bónuszok</SectionTitle>
              <ul className="divide-y divide-border/60 rounded-lg border border-border/60">
                {q.data.bonuses.map((b, i) => (
                  <li key={i} className="flex items-center justify-between px-3 py-2 text-sm">
                    <span>{b.label}</span>
                    <span className="font-medium tabular-nums text-primary">+{b.points}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {q.data && q.data.tips.length > 0 && (
            <div className="space-y-2">
              <SectionTitle>Tippek</SectionTitle>
              {q.data.tips.map((t, i) => (
                <TipRow key={i} tip={t} />
              ))}
            </div>
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}

function SummaryTile({ label, value, primary }: { label: string; value: number; primary?: boolean }) {
  return (
    <div className={cn("rounded-lg border p-2 text-center", primary ? "border-primary/40 bg-primary/10" : "border-border/60 bg-background/40")}>
      <div className={cn("text-xl font-semibold tabular-nums", primary && "text-primary")}>{value}</div>
      <div className="text-[0.65rem] uppercase tracking-wide text-muted-foreground">{label}</div>
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return <h4 className="mb-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">{children}</h4>;
}

function TipRow({ tip }: { tip: PointsBreakdownTipDto }) {
  const hasResult = tip.scoreA !== null && tip.scoreB !== null;
  return (
    <div className="rounded-lg border border-border/60 p-3">
      <div className="flex items-center gap-2 text-sm">
        <span className="hidden w-24 shrink-0 text-xs text-muted-foreground sm:inline">{stageLabel(tip.stage)}</span>
        <div className="flex min-w-0 flex-1 items-center justify-center gap-1.5">
          <TeamFlag name={tip.teamA} flag={tip.teamAFlag} className="size-5" />
          <span className="truncate">{tip.teamA}</span>
          <span className="mx-1 font-semibold tabular-nums">
            {hasResult ? `${tip.scoreA}–${tip.scoreB}` : "– –"}
          </span>
          <span className="truncate">{tip.teamB}</span>
          <TeamFlag name={tip.teamB} flag={tip.teamBFlag} className="size-5" />
        </div>
        <div className="flex w-16 shrink-0 items-center justify-end gap-1">
          {tip.isTuti && <Star className="size-3.5 fill-warning text-warning" />}
          {tip.isBullseye && <Target className="size-3.5 text-success" />}
          <span className="font-semibold tabular-nums">{tip.points}</span>
        </div>
      </div>

      <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 pl-0 sm:pl-24">
        <span className="text-xs text-muted-foreground">
          Tipped: <span className="tabular-nums text-foreground">{tip.tipA ?? "–"}–{tip.tipB ?? "–"}</span>
        </span>
        {tip.rules.map((r, i) => (
          <span key={i} className="text-xs text-muted-foreground">
            {r.description} <span className="font-medium text-primary">+{r.points}</span>
          </span>
        ))}
      </div>
    </div>
  );
}
